import React from 'react';
import { Box, Button, ButtonProps, Link, useColorModeValue } from '@chakra-ui/react';

type ArcButtonProps = {
  arcColor: string;
  children: React.ReactNode | React.ReactNode[];
  isFixedAtScreenBottom?: boolean;
} & ButtonProps;

const COLOR_BUTTON_FILL = '#505559';
const COLOR_FIXED_BORDER = '#3DCD57';

export function ArcButton({ arcColor, children, isFixedAtScreenBottom = false, ...rest }: ArcButtonProps) {
  const buttonTheme = useColorModeValue(
    {
      variant: 'outline',
      background: COLOR_BUTTON_FILL,
      _hover: {
        background: COLOR_BUTTON_FILL,
      },
      borderColor: COLOR_BUTTON_FILL,
      color: 'white',
    },
    {
      variant: 'solid',
      background: COLOR_BUTTON_FILL,
      _hover: {
        background: COLOR_BUTTON_FILL,
      },
      borderColor: COLOR_BUTTON_FILL,
      color: 'white',
    }
  );

  const fixedFooterBackground = useColorModeValue('white', '#111111');
  const fixedFooterShadow = useColorModeValue('0 -4px 12px rgba(0, 0, 0, 0.12)', '0 -4px 12px rgba(0, 0, 0, 0.4)');

  const button = (
    <Button
      py={6}
      zIndex={5}
      overflow="hidden"
      minWidth="100px"
      position={'relative'}
      fontWeight={500}
      rounded={24}
      {...(isFixedAtScreenBottom && rest.w === undefined ? { w: 'calc(100% - var(--chakra-space-12))' } : {})}
      {...(isFixedAtScreenBottom && rest.mx === undefined ? { mx: 6 } : {})}
      {...(isFixedAtScreenBottom && rest.mt === undefined ? { mt: 4 } : {})}
      {...buttonTheme}
      {...rest}
    >
      <Box zIndex={8}>{children}</Box>
      <Box
        zIndex={6}
        position="absolute"
        w="40%"
        h="90%"
        border="2px solid"
        borderColor={`${arcColor} ${arcColor} ${arcColor} transparent`}
        background={COLOR_BUTTON_FILL}
        borderTopRightRadius="100px"
        borderBottomRightRadius="100px"
        right="2px"
      />
      <Box zIndex={7} position="absolute" w={'100%'} h="90%" background={COLOR_BUTTON_FILL} right="25px" />
    </Button>
  );

  if (!isFixedAtScreenBottom) {
    return button;
  }

  return (
    <Box
      bottom={0}
      left={0}
      right={0}
      width="100%"
      display="flex"
      alignItems="center"
      position={'fixed'}
      justifyContent="center"
      flexShrink={0}
      pb="calc(var(--chakra-space-4) + env(safe-area-inset-bottom))"
      borderTop="2px solid"
      borderColor={COLOR_FIXED_BORDER}
      background={fixedFooterBackground}
      boxShadow={fixedFooterShadow}
      zIndex={5}
    >
      {button}
    </Box>
  );
}

type ArcButtonWithLinkProps = ArcButtonProps & { href: string };

export const ArcButtonWithLink = ({ href, ...arcButtonProps }: ArcButtonWithLinkProps) => {
  return (
    <Link href={href}>
      <ArcButton {...arcButtonProps} />
    </Link>
  );
};
